import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

/**
 * Determina si la ruta actual es un dashboard de Biller
 * El theming SOLO afecta a dashboards de Billers, no al resto del sistema
 */
const isBillerDashboardRoute = (pathname) => {
  return pathname.startsWith('/billers/') && pathname.includes('/dashboard');
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isThemingEnabled, setIsThemingEnabled] = useState(false);

  // Verificar si estamos en una ruta de Biller Dashboard
  useEffect(() => {
    const shouldEnableTheming = isBillerDashboardRoute(location.pathname);
    setIsThemingEnabled(shouldEnableTheming);

    // Actualizar atributo data-themed
    const root = document.documentElement;
    root.setAttribute('data-themed', shouldEnableTheming ? 'true' : 'false');

    // Si salimos del dashboard de biller, restaurar tema por defecto
    if (!shouldEnableTheming && theme) {
      const defaultTheme = getDefaultTheme();
      applyThemeToDOM(defaultTheme);
    }
  }, [location.pathname]);

  const loadPublisherTheme = useCallback(async () => {

    // Solo cargar tema si estamos en dashboard de biller
    if (!isThemingEnabled) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      setLoading(false);
      return;
    }

    // Si no hay usuario, usar tema por defecto
    if (!user) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      applyThemeToDOM(defaultTheme);
      setLoading(false);
      return;
    }

    // Obtener publisher_id desde la URL del Biller Dashboard
    // URL format: /billers/:biller_id/dashboard
    // Double-check que realmente estamos en una ruta de biller antes de extraer el ID
    if (!location.pathname.startsWith('/billers/')) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      applyThemeToDOM(defaultTheme);
      setLoading(false);
      return;
    }

    const pathParts = location.pathname.split('/');
    const billerIdFromUrl = pathParts[2]; // /billers/[BILLER_ID]/dashboard


    if (!billerIdFromUrl) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      applyThemeToDOM(defaultTheme);
      setLoading(false);
      return;
    }

    // Obtener publisher_id desde el biller
    // Usamos el endpoint de dashboard/info que ya existe y devuelve publisher_id
    let publisherId = null;

    try {
      // Intentar obtener publisher_id del biller desde dashboard/info
      const billerResponse = await api.get(`/billers/${billerIdFromUrl}/dashboard/info`);
      publisherId = billerResponse.data?.data?.publisher_id;


      if (!publisherId) {
        // Fallback: usar assigned_publisher_id del usuario
        publisherId = user.assigned_publisher_id || user.scope?.publisher_id;
      }
    } catch (error) {
      console.error('🎨 [ThemeContext] Error fetching biller publisher:', error);
      // Fallback: usar assigned_publisher_id del usuario
      publisherId = user.assigned_publisher_id || user.scope?.publisher_id;
    }

    // Si no tiene publisher asignado, usar tema por defecto
    if (!publisherId) {
      const defaultTheme = getDefaultTheme();
      setTheme(defaultTheme);
      applyThemeToDOM(defaultTheme);
      setLoading(false);
      return;
    }

    // Intentar cargar desde localStorage primero (caché)
    const cacheKey = `publisher_theme_${publisherId}`;
    let cachedTheme = null;

    try {
      cachedTheme = localStorage.getItem(cacheKey);

      if (cachedTheme) {
        const parsed = JSON.parse(cachedTheme);
        setTheme(parsed);
        applyThemeToDOM(parsed);
      }

      // Cargar desde API (actualización en background)
      const response = await api.get(`/admin/publishers/${publisherId}/theme`);
      const freshTheme = response.data.theme;


      // Actualizar caché
      localStorage.setItem(cacheKey, JSON.stringify(freshTheme));

      // Actualizar estado
      setTheme(freshTheme);
      applyThemeToDOM(freshTheme);

    } catch (error) {
      console.error('🎨 [ThemeContext] Error loading publisher theme:', error);

      // Si falla y no hay caché, usar tema por defecto
      if (!cachedTheme) {
        const defaultTheme = getDefaultTheme();
        setTheme(defaultTheme);
        applyThemeToDOM(defaultTheme);
      }
    } finally {
      setLoading(false);
    }
  }, [user, location.pathname, isThemingEnabled]);

  useEffect(() => {
    loadPublisherTheme();
  }, [loadPublisherTheme, isThemingEnabled]);

  const applyThemeToDOM = (themeConfig) => {
    if (!themeConfig) {
      console.warn('🎨 [ThemeContext] applyThemeToDOM called with no theme config');
      return;
    }


    const root = document.documentElement;

    // Marcar que el theming está activo para que los estilos CSS lo detecten
    root.setAttribute('data-themed', isThemingEnabled ? 'true' : 'false');

    // Aplicar colores principales
    root.style.setProperty('--color-primary', themeConfig.primary_color || '#3B82F6');
    root.style.setProperty('--color-primary-hover', themeConfig.primary_hover || '#2563EB');
    root.style.setProperty('--color-secondary', themeConfig.secondary_color || '#6366F1');
    root.style.setProperty('--color-secondary-hover', themeConfig.secondary_hover || '#4F46E5');

    // Colores de estado
    root.style.setProperty('--color-success', themeConfig.success_color || '#10B981');
    root.style.setProperty('--color-warning', themeConfig.warning_color || '#F59E0B');
    root.style.setProperty('--color-danger', themeConfig.danger_color || '#EF4444');
    root.style.setProperty('--color-info', themeConfig.info_color || '#3B82F6');

    // Colores de texto
    root.style.setProperty('--color-text-primary', themeConfig.text_primary || '#1F2937');
    root.style.setProperty('--color-text-secondary', themeConfig.text_secondary || '#6B7280');
    root.style.setProperty('--color-text-muted', themeConfig.text_muted || '#9CA3AF');

    // Colores de fondo
    root.style.setProperty('--color-bg-primary', themeConfig.bg_primary || '#FFFFFF');
    root.style.setProperty('--color-bg-secondary', themeConfig.bg_secondary || '#F9FAFB');
    root.style.setProperty('--color-bg-tertiary', themeConfig.bg_tertiary || '#F3F4F6');

    // Bordes
    root.style.setProperty('--color-border', themeConfig.border_color || '#E5E7EB');
    root.style.setProperty('--border-radius-sm', themeConfig.border_radius_sm || '0.25rem');
    root.style.setProperty('--border-radius-md', themeConfig.border_radius_md || '0.5rem');
    root.style.setProperty('--border-radius-lg', themeConfig.border_radius_lg || '1rem');

    // Sombras
    root.style.setProperty('--shadow-sm', themeConfig.shadow_sm || '0 1px 2px 0 rgba(0,0,0,0.05)');
    root.style.setProperty('--shadow-md', themeConfig.shadow_md || '0 4px 6px -1px rgba(0,0,0,0.1)');
    root.style.setProperty('--shadow-lg', themeConfig.shadow_lg || '0 10px 15px -3px rgba(0,0,0,0.1)');

    // Tipografía
    root.style.setProperty('--font-family-primary', themeConfig.font_family_primary || 'Inter');
    root.style.setProperty('--font-family-secondary', themeConfig.font_family_secondary || 'Inter');
    root.style.setProperty('--font-size-base', themeConfig.font_size_base || '16px');
    root.style.setProperty('--font-weight-normal', themeConfig.font_weight_normal || '400');
    root.style.setProperty('--font-weight-medium', themeConfig.font_weight_medium || '500');
    root.style.setProperty('--font-weight-bold', themeConfig.font_weight_bold || '700');

    // Cambiar favicon dinámicamente
    if (themeConfig.favicon_url) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'shortcut icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.type = 'image/x-icon';
      link.href = themeConfig.favicon_url;
    }

    // Aplicar CSS personalizado si existe
    if (themeConfig.custom_css) {
      let styleTag = document.getElementById('custom-publisher-styles');

      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'custom-publisher-styles';
        document.head.appendChild(styleTag);
      }

      styleTag.innerHTML = themeConfig.custom_css;
    }
  };

  const getDefaultTheme = () => ({
    publisher_id: 'default',
    theme_name: 'default',
    is_active: 1,
    primary_color: '#3B82F6',
    primary_hover: '#2563EB',
    secondary_color: '#6366F1',
    secondary_hover: '#4F46E5',
    success_color: '#10B981',
    warning_color: '#F59E0B',
    danger_color: '#EF4444',
    info_color: '#3B82F6',
    text_primary: '#1F2937',
    text_secondary: '#6B7280',
    text_muted: '#9CA3AF',
    bg_primary: '#FFFFFF',
    bg_secondary: '#F9FAFB',
    bg_tertiary: '#F3F4F6',
    border_color: '#E5E7EB',
    border_radius_sm: '0.25rem',
    border_radius_md: '0.5rem',
    border_radius_lg: '1rem',
    shadow_sm: '0 1px 2px 0 rgba(0,0,0,0.05)',
    shadow_md: '0 4px 6px -1px rgba(0,0,0,0.1)',
    shadow_lg: '0 10px 15px -3px rgba(0,0,0,0.1)',
    font_family_primary: 'Inter',
    font_family_secondary: 'Inter',
    font_size_base: '16px',
    font_weight_normal: 400,
    font_weight_medium: 500,
    font_weight_bold: 700,
    logo_url: null,
    logo_alt: 'ACMW',
    favicon_url: null,
    login_background_url: null,
    custom_css: null
  });

  // Helper para acceder rápido a colores
  const colors = theme ? {
    primary: theme.primary_color || '#3B82F6',
    primaryHover: theme.primary_hover || '#2563EB',
    secondary: theme.secondary_color || '#6366F1',
    secondaryHover: theme.secondary_hover || '#4F46E5',
    success: theme.success_color || '#10B981',
    warning: theme.warning_color || '#F59E0B',
    danger: theme.danger_color || '#EF4444',
    info: theme.info_color || '#3B82F6',
    textPrimary: theme.text_primary || '#1F2937',
    textSecondary: theme.text_secondary || '#6B7280',
    textMuted: theme.text_muted || '#9CA3AF',
    bgPrimary: theme.bg_primary || '#FFFFFF',
    bgSecondary: theme.bg_secondary || '#F9FAFB',
    bgTertiary: theme.bg_tertiary || '#F3F4F6',
    border: theme.border_color || '#E5E7EB',
  } : null;

  // Helper para assets
  const assets = theme ? {
    logo: theme.logo_url,
    logoAlt: theme.logo_alt || 'Logo',
    favicon: theme.favicon_url,
    loginBackground: theme.login_background_url,
  } : null;

  // Helper para tipografía
  const typography = theme ? {
    fontPrimary: theme.font_family_primary || 'Inter',
    fontSecondary: theme.font_family_secondary || 'Inter',
    fontSize: theme.font_size_base || '16px',
    weightNormal: theme.font_weight_normal || 400,
    weightMedium: theme.font_weight_medium || 500,
    weightBold: theme.font_weight_bold || 700,
  } : null;

  const value = {
    theme,
    loading,
    colors,
    assets,
    typography,
    isThemingEnabled,
    reloadTheme: loadPublisherTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;

// Export helper para usar en componentes
export { isBillerDashboardRoute };
